import { supabase } from '@/core/supabase/client';
import { guardarEnCache, leerDeCache } from './almacen';
import type { OrganizationService } from '@/features/catalog/catalog';
import type { DestinatarioFactura } from '@/features/clients/clients';
import type { Client, Establishment, OrganizationSettings } from '@/features/clients/clients';
import type { Participant, SplitTemplate, WorkDefaults } from '@/features/works/works';

/**
 * La copia operativa: todo lo que hace falta para cargar un trabajo sin señal.
 *
 * ## Por qué una foto y no diez cachés
 *
 * Antes había una lista de claves «cacheables» —clientes, establecimientos,
 * servicios, participantes— configurada en TanStack Query. El problema es que
 * esas claves **no existían**: sólo tres lugares de la aplicación usan TanStack
 * Query, y el buscador de clientes, el formulario de alta y la pantalla de
 * clientes traían sus datos con llamadas directas. El caché estaba preparado
 * para consumidores que nunca llegaron, así que sin señal las tres pantallas
 * mostraban `TypeError: Failed to fetch`.
 *
 * Diez cachés independientes además se desincronizan entre sí: los honorarios
 * de ayer con los participantes de hoy, y un reparto que apunta a alguien que
 * ya no está. Una foto con fecha se entiende de una: esto es lo que había en el
 * teléfono el día tal.
 *
 * ## Por qué una sola lectura
 *
 * La primera versión traía la foto con once peticiones en paralelo, y aun así
 * le faltaba lo más útil: los valores habituales de cada cliente. Ahora la trae
 * `get_offline_operational_bundle`, una función del servidor que devuelve todo
 * junto. No es sólo por la cantidad de pedidos: once respuestas son once
 * momentos distintos de la base, y una sola es una sola. Guardarla es entonces
 * un único `put` —el teléfono nunca queda con la mitad de los datos nuevos y la
 * mitad viejos—.
 *
 * ## Los defaults se heredan igual que con señal
 *
 * `defaults` trae, por cliente y por cada uno de sus establecimientos, lo mismo
 * que devolvería `work_defaults` en ese momento: movilidad, facturador y
 * reparto habituales. Es la diferencia entre trabajar sin señal y trabajar sin
 * señal *y sin los automatismos*, que es la versión manual de la aplicación
 * ofrecida justo cuando menos se puede revisar nada.
 *
 * Lo calcula el servidor llamando a la MISMA función que se llama online, no
 * una copia de su lógica en TypeScript: una segunda implementación de la
 * herencia Organización → Cliente → Establecimiento sería una segunda verdad, y
 * la verdad de este proyecto es PostgreSQL.
 *
 * ## Qué NO es
 *
 * No autoriza nada. Es una copia de lecturas que el servidor ya autorizó una
 * vez —RLS decidió qué entraba acá— y sirve para MOSTRAR y para PRELLENAR. Toda
 * escritura vuelve a pasar por el servidor, que verifica de nuevo con los
 * permisos de ese momento y no con los del día que se bajó la copia. Un
 * dispositivo que recuerda que su dueño era administrador no puede escribir
 * nada por eso.
 */
export interface BundleOperativo {
  /**
   * Versión del formato.
   *
   * Una copia bajada por una versión anterior de la aplicación puede no tener
   * campos que ahora se dan por hechos. Leerla a medias sería peor que no
   * tenerla: se descarta y se baja de nuevo.
   */
  esquema: number;
  organizationId: string;
  /** Cuándo se trajo esta foto. Es lo que permite decir «datos del martes». */
  sincronizadoEn: number;
  clientes: Client[];
  /** Por `client_id`. Es lo que llena el segundo renglón del buscador. */
  establecimientos: Record<string, Establishment[]>;
  servicios: OrganizationService[];
  participantes: Participant[];
  repartos: SplitTemplate[];
  ajustes: OrganizationSettings | null;
  /** Precio del novillo y del combustible por período, `"2026-9"` → centavos. */
  precios: Record<string, { steer: number | null; fuel: number | null }>;
  /**
   * Los valores habituales, por `claveDefaults(cliente, establecimiento)`.
   * Con `null` de establecimiento están los del cliente a secas.
   */
  defaults: Record<string, WorkDefaults>;
  /** A nombre de quién se le factura habitualmente a cada cliente. */
  destinatarios: Record<string, DestinatarioFactura[]>;
}

/** La versión de formato que entiende esta aplicación. */
export const ESQUEMA_BUNDLE = 1;

const clave = (orgId: string) => `bundle:${orgId}`;

/** La clave de un período dentro de `precios`. */
export const clavePeriodo = (year: number, month: number) => `${year}-${month}`;

/**
 * La clave de unos defaults.
 *
 * Cliente y establecimiento juntos, porque el establecimiento puede pisar los
 * kilómetros, la movilidad y a quién se le paga. Sin establecimiento elegido
 * quedan los del cliente, que es lo que contesta `work_defaults(c, null)`.
 */
export const claveDefaults = (clientId: string, establishmentId: string | null) =>
  `${clientId}|${establishmentId ?? ''}`;

/** Lo que devuelve la función del servidor, antes de acomodarlo. */
interface BundleCrudo {
  schema_version: number;
  organization_id: string;
  fetched_at: string;
  settings: OrganizationSettings | null;
  clients: Client[] | null;
  establishments: Establishment[] | null;
  services: OrganizationService[] | null;
  participants: Participant[] | null;
  split_templates: SplitTemplate[] | null;
  invoice_recipients: ({ client_id: string } & DestinatarioFactura)[] | null;
  work_defaults:
    { client_id: string; establishment_id: string | null; defaults: WorkDefaults }[] | null;
  prices: Record<string, { steer: number | null; fuel: number | null }> | null;
}

/**
 * Traer la foto del servidor. Una llamada, una transacción, un momento.
 *
 * Si falla, falla entera y no se guarda nada: media copia nueva mezclada con
 * media vieja es exactamente el estado que no se puede explicar después.
 */
export async function descargarBundle(
  organizationId: string,
  hoy = new Date(),
): Promise<BundleOperativo> {
  const { data, error } = await supabase.rpc('get_offline_operational_bundle', {
    p_org: organizationId,
    p_year: hoy.getFullYear(),
    p_month: hoy.getMonth() + 1,
  });
  if (error) throw error;

  const crudo = data as BundleCrudo;

  const establecimientos: Record<string, Establishment[]> = {};
  for (const e of crudo.establishments ?? []) {
    (establecimientos[e.client_id] ??= []).push(e);
  }

  const destinatarios: Record<string, DestinatarioFactura[]> = {};
  for (const r of crudo.invoice_recipients ?? []) {
    (destinatarios[r.client_id] ??= []).push({
      name: r.name,
      percentage: Number(r.percentage),
      sort_order: r.sort_order ?? 0,
    });
  }

  const defaults: Record<string, WorkDefaults> = {};
  for (const d of crudo.work_defaults ?? []) {
    defaults[claveDefaults(d.client_id, d.establishment_id)] = d.defaults;
  }

  return {
    esquema: crudo.schema_version,
    organizationId,
    sincronizadoEn: Date.now(),
    clientes: crudo.clients ?? [],
    establecimientos,
    servicios: crudo.services ?? [],
    participantes: crudo.participants ?? [],
    repartos: crudo.split_templates ?? [],
    ajustes: crudo.settings,
    precios: crudo.prices ?? {},
    defaults,
    destinatarios,
  };
}

export async function guardarBundle(b: BundleOperativo): Promise<void> {
  await guardarEnCache(clave(b.organizationId), b);
}

/**
 * Una copia de un formato anterior se aprovecha, no se tira.
 *
 * La primera versión de esto descartaba entera cualquier copia cuyo esquema no
 * fuera el actual. Está mal, y se vio en el teléfono: al actualizar, la copia
 * que ya estaba guardada dejó de leerse y la pantalla pasó a decir «todavía no
 * hay clientes descargados», que es el peor estado posible —sin clientes no
 * hay trabajo que cargar—.
 *
 * Los grados de degradación no son lo mismo, y confundirlos cuesta caro:
 *
 *   · una copia SIN los valores habituales todavía sirve para todo lo demás:
 *     encontrar al cliente, elegir el campo, cargar los honorarios y guardar.
 *     Falta el prellenado, y eso se avisa;
 *   · no tener copia no sirve para nada.
 *
 * Así que lo que falta se completa vacío y se sigue. Lo único sin lo que no se
 * puede hacer nada es la lista de clientes: sin ella no hay copia.
 */
function normalizar(b: BundleOperativo | null): BundleOperativo | null {
  if (!b || !Array.isArray(b.clientes)) return null;
  if (b.esquema === ESQUEMA_BUNDLE) return b;
  return {
    ...b,
    esquema: b.esquema ?? 0,
    establecimientos: b.establecimientos ?? {},
    servicios: b.servicios ?? [],
    participantes: b.participantes ?? [],
    repartos: b.repartos ?? [],
    precios: b.precios ?? {},
    // Lo que la copia vieja no tenía. Vacío significa «no los tengo», y de ahí
    // sale el aviso del formulario; no significa «este cliente no tiene».
    defaults: b.defaults ?? {},
    destinatarios: b.destinatarios ?? {},
  };
}

export async function leerBundle(organizationId: string): Promise<BundleOperativo | null> {
  try {
    const fila = await leerDeCache<BundleOperativo>(clave(organizationId));
    return normalizar(fila?.valor ?? null);
  } catch {
    return null;
  }
}

/** ¿Esta copia trae los valores habituales, o es de antes de que existieran? */
export function laCopiaTraeDefaults(b: BundleOperativo | null): boolean {
  return !!b && b.esquema === ESQUEMA_BUNDLE;
}

/**
 * Los valores habituales de este cliente, tal como estaban al último sync.
 *
 * Cae al nivel del cliente si no hay nada guardado para ese establecimiento:
 * es la misma jerarquía que aplica el servidor, un escalón más arriba.
 */
export function defaultsDelBundle(
  b: BundleOperativo | null,
  clientId: string,
  establishmentId: string | null,
): WorkDefaults | null {
  if (!b) return null;
  return (
    b.defaults[claveDefaults(clientId, establishmentId)] ??
    b.defaults[claveDefaults(clientId, null)] ??
    null
  );
}

/** A nombre de quién se le factura a este cliente, según la copia local. */
export function destinatariosDelBundle(
  b: BundleOperativo | null,
  clientId: string,
): DestinatarioFactura[] {
  return b?.destinatarios[clientId] ?? [];
}

// ---------------------------------------------------------------------------
// Buscar dentro de la foto
// ---------------------------------------------------------------------------

/** Sin acentos y en minúsculas, para que «Maria» encuentre «María». */
function plano(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * El mismo buscador que el del servidor, contra la copia local.
 *
 * Busca por nombre de cliente **y por nombre de campo**, igual que
 * `search_clients`: quien busca «El Trébol» está buscando el campo, y que no
 * aparezca porque el cliente se llama distinto se lee como un buscador roto.
 * Cuando la coincidencia fue por el campo, se dice cuál, para que no parezca
 * que devolvió otra cosa.
 */
export function buscarEnBundle(b: BundleOperativo | null, termino: string): Client[] {
  if (!b) return [];
  const t = plano(termino.trim());
  if (!t) return [];

  const encontrados: Client[] = [];
  for (const c of b.clientes) {
    if (!c.is_active) continue;
    if (plano(c.name).includes(t)) {
      encontrados.push({ ...c, matched_establishment: null });
      continue;
    }
    const campo = (b.establecimientos[c.id] ?? []).find((e) => plano(e.name).includes(t));
    if (campo) encontrados.push({ ...c, matched_establishment: campo.name });
  }
  return encontrados;
}
